import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useUserProfile } from '../contexts/UserContext';
import {
  getAllPosts, createPost, updatePost, deletePost,
  togglePublish, BLOG_CATEGORIES,
} from '../services/blogService';

/* ── Markdown Preview (simple) ── */
function renderMarkdown(md) {
  if (!md) return '';
  let html = md
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/^\> (.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');
  return `<p>${html}</p>`;
}

/* ── Emojis for cover ── */
const EMOJIS = ['📝', '🏦', '⚡', '📊', '💡', '🎯', '🔑', '📈', '💰', '🏠', '📋', '🛡️', '🧮', '📉', '🤝'];

const GRADIENTS = [
  'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #0A0F1E 100%)',
  'linear-gradient(135deg, #4c1d95 0%, #6d28d9 50%, #0A0F1E 100%)',
  'linear-gradient(135deg, #78350f 0%, #b45309 50%, #0A0F1E 100%)',
  'linear-gradient(135deg, #065f46 0%, #059669 50%, #0A0F1E 100%)',
  'linear-gradient(135deg, #831843 0%, #be185d 50%, #0A0F1E 100%)',
  'linear-gradient(135deg, #1e293b 0%, #334155 50%, #0A0F1E 100%)',
];

export default function BlogAdminPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, loading: profileLoading } = useUserProfile();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null = list, 'new' = create, postId = edit
  const [form, setForm] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [busca, setBusca] = useState('');

  function getEmptyForm() {
    return {
      title: '', subtitle: '', category: BLOG_CATEGORIES[0].label,
      categoryCor: BLOG_CATEGORIES[0].cor, content: '', tags: '',
      coverEmoji: '📝', coverGradient: GRADIENTS[0],
      author: { name: 'David Oliveira', role: 'CEO — SimulaBank', ini: 'DO', cor: '#3B82F6' },
      published: false, metaTitle: '', metaDescription: '',
    };
  }

  const loadPosts = useCallback(async () => {
    setLoading(true);
    const data = await getAllPosts();
    setPosts(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleNew = () => {
    setForm(getEmptyForm());
    setEditing('new');
    setPreview(false);
  };

  const handleEdit = (post) => {
    setForm({
      ...post,
      tags: (post.tags || []).join(', '),
    });
    setEditing(post.id);
    setPreview(false);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Título é obrigatório');
    setSaving(true);
    try {
      const data = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };

      if (editing === 'new') {
        const { id } = await createPost(data);
        alert('Post criado!');
      } else {
        await updatePost(editing, data);
        alert('Post atualizado!');
      }
      await loadPosts();
      setEditing(null);
    } catch (e) {
      alert('Erro: ' + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Excluir este post permanentemente?')) return;
    await deletePost(postId);
    await loadPosts();
  };

  const handleTogglePublish = async (post) => {
    await togglePublish(post.id, !post.published);
    await loadPosts();
  };

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const updateAuthor = (field, value) => setForm(f => ({ ...f, author: { ...f.author, [field]: value } }));

  const filtered = posts.filter(p =>
    `${p.title} ${p.category} ${p.tags.join(' ')}`.toLowerCase().includes(busca.toLowerCase())
  );

  const labelStyle = {
    display: 'block', fontSize: '0.72rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    color: 'var(--text-muted)', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)',
    borderRadius: 'var(--radius-xs)', fontFamily: 'var(--font)', fontSize: '0.88rem',
    outline: 'none', background: 'var(--white)', color: 'var(--text)',
  };

  /* ════════════════════════════════════
     GUARD — Super Admin only
  ════════════════════════════════════ */
  if (profileLoading) {
    return (
      <DashboardLayout title="Editor Blog" subtitle="Gerenciar conteúdo">
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>Carregando...</div>
      </DashboardLayout>
    );
  }

  if (!isSuperAdmin) {
    return (
      <DashboardLayout title="Editor Blog" subtitle="Gerenciar conteúdo">
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>
          <span style={{ fontSize: '3rem' }}>🛡️</span>
          <p style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 16 }}>Acesso Restrito</p>
          <p style={{ fontSize: '0.88rem', marginTop: 4 }}>
            Apenas Super Administradores podem acessar o editor de blog.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  /* ════════════════════════════════════
     EDITOR VIEW
  ════════════════════════════════════ */
  if (editing !== null) {
    return (
      <DashboardLayout title="Editor Blog" subtitle={editing === 'new' ? 'Novo post' : 'Editar post'}>
        {/* Topbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 24, flexWrap: 'wrap', gap: 12,
        }}>
          <Button variant="ghost" onClick={() => setEditing(null)}>← Voltar</Button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="ghost" onClick={() => setPreview(!preview)}>
              {preview ? '✏️ Editar' : '👁️ Preview'}
            </Button>
            <Button
              variant={form.published ? 'ghost' : 'primary'}
              onClick={() => updateField('published', !form.published)}
            >
              {form.published ? '📤 Publicado' : '📝 Rascunho'}
            </Button>
            <Button variant="accent" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : '💾 Salvar'}
            </Button>
          </div>
        </div>

        {preview ? (
          /* ── PREVIEW ── */
          <Card style={{ padding: 32, maxWidth: 720 }}>
            <div style={{ marginBottom: 16 }}>
              <span style={{
                fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.1em', padding: '4px 12px', borderRadius: 100,
                background: form.categoryCor + '18', color: form.categoryCor,
                border: `1px solid ${form.categoryCor}30`,
              }}>{form.category}</span>
            </div>
            <h1 style={{
              fontFamily: "'Instrument Serif', serif", fontSize: '2.2rem',
              lineHeight: 1.15, color: 'var(--primary)', marginBottom: 16,
            }}>{form.title || 'Sem título'}</h1>
            <p style={{ fontSize: '1.05rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 24 }}>
              {form.subtitle}
            </p>
            <div style={{
              width: '100%', aspectRatio: '16/8', borderRadius: 'var(--radius)',
              background: form.coverGradient, display: 'flex', alignItems: 'center',
              justifyContent: 'center', marginBottom: 32,
            }}>
              <span style={{ fontSize: '4rem' }}>{form.coverEmoji}</span>
            </div>
            <div
              style={{ fontSize: '1rem', lineHeight: 1.85, color: '#2d3748' }}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(form.content) }}
            />
            {form.tags && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 32 }}>
                {form.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                  <span key={t} style={{
                    fontSize: '0.75rem', fontWeight: 600, padding: '5px 12px',
                    borderRadius: 100, background: 'var(--surface)', border: '1px solid var(--border)',
                    color: 'var(--text-muted)',
                  }}>{t}</span>
                ))}
              </div>
            )}
          </Card>
        ) : (
          /* ── EDITOR ── */
          <div className="blog-editor-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
            {/* Main */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <Card style={{ padding: 24 }}>
                <label style={labelStyle}>Título</label>
                <input
                  style={{ ...inputStyle, fontSize: '1.1rem', fontWeight: 700 }}
                  value={form.title}
                  onChange={e => updateField('title', e.target.value)}
                  placeholder="Título do artigo..."
                />
              </Card>

              <Card style={{ padding: 24 }}>
                <label style={labelStyle}>Subtítulo</label>
                <input
                  style={inputStyle}
                  value={form.subtitle}
                  onChange={e => updateField('subtitle', e.target.value)}
                  placeholder="Descrição curta do artigo..."
                />
              </Card>

              <Card style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Conteúdo (Markdown)</label>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {(form.content || '').split(/\s+/).filter(Boolean).length} palavras
                  </span>
                </div>
                <textarea
                  value={form.content}
                  onChange={e => updateField('content', e.target.value)}
                  placeholder={'## Seção 1\n\nEscreva o conteúdo do artigo em **markdown**...\n\n- Use listas\n- **Negrito** e *itálico*\n- `código inline`\n\n> Citações com >'}
                  style={{
                    ...inputStyle, minHeight: 400, resize: 'vertical',
                    fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', lineHeight: 1.7,
                  }}
                />
              </Card>

              <Card style={{ padding: 24 }}>
                <label style={labelStyle}>Tags (separadas por vírgula)</label>
                <input
                  style={inputStyle}
                  value={form.tags}
                  onChange={e => updateField('tags', e.target.value)}
                  placeholder="SAC, Price, Financiamento, Correspondente"
                />
              </Card>

              {/* SEO */}
              <Card style={{ padding: 24 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>🔍 SEO</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Meta Title</label>
                    <input
                      style={inputStyle}
                      value={form.metaTitle}
                      onChange={e => updateField('metaTitle', e.target.value)}
                      placeholder={form.title || 'Título para SEO...'}
                    />
                    <span style={{ fontSize: '0.68rem', color: (form.metaTitle || form.title).length > 60 ? 'var(--danger)' : 'var(--text-muted)' }}>
                      {(form.metaTitle || form.title).length}/60
                    </span>
                  </div>
                  <div>
                    <label style={labelStyle}>Meta Description</label>
                    <textarea
                      style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                      value={form.metaDescription}
                      onChange={e => updateField('metaDescription', e.target.value)}
                      placeholder={form.subtitle || 'Descrição para SEO...'}
                    />
                    <span style={{ fontSize: '0.68rem', color: (form.metaDescription || form.subtitle).length > 160 ? 'var(--danger)' : 'var(--text-muted)' }}>
                      {(form.metaDescription || form.subtitle).length}/160
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Category */}
              <Card style={{ padding: 20 }}>
                <label style={labelStyle}>Categoria</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {BLOG_CATEGORIES.map(cat => (
                    <button
                      key={cat.label}
                      onClick={() => { updateField('category', cat.label); updateField('categoryCor', cat.cor); }}
                      style={{
                        padding: '8px 12px', borderRadius: 'var(--radius-xs)', border: '1.5px solid',
                        borderColor: form.category === cat.label ? cat.cor : 'var(--border)',
                        background: form.category === cat.label ? cat.cor + '15' : 'var(--white)',
                        color: form.category === cat.label ? cat.cor : 'var(--text-muted)',
                        fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                        fontFamily: 'var(--font)', textAlign: 'left',
                      }}
                    >{cat.label}</button>
                  ))}
                </div>
              </Card>

              {/* Cover */}
              <Card style={{ padding: 20 }}>
                <label style={labelStyle}>Capa</label>
                <div style={{
                  width: '100%', aspectRatio: '16/9', borderRadius: 'var(--radius-sm)',
                  background: form.coverGradient, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', marginBottom: 14,
                }}>
                  <span style={{ fontSize: '2.5rem' }}>{form.coverEmoji}</span>
                </div>

                <label style={{ ...labelStyle, marginTop: 8 }}>Emoji</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                  {EMOJIS.map(e => (
                    <button
                      key={e}
                      onClick={() => updateField('coverEmoji', e)}
                      style={{
                        width: 36, height: 36, borderRadius: 8, border: '1.5px solid',
                        borderColor: form.coverEmoji === e ? 'var(--accent)' : 'var(--border)',
                        background: form.coverEmoji === e ? 'rgba(0,214,143,0.1)' : 'var(--white)',
                        fontSize: '1.1rem', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >{e}</button>
                  ))}
                </div>

                <label style={labelStyle}>Gradiente</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {GRADIENTS.map((g, i) => (
                    <button
                      key={i}
                      onClick={() => updateField('coverGradient', g)}
                      style={{
                        width: 40, height: 28, borderRadius: 6, background: g, cursor: 'pointer',
                        border: form.coverGradient === g ? '2px solid var(--accent)' : '2px solid transparent',
                      }}
                    />
                  ))}
                </div>
              </Card>

              {/* Author */}
              <Card style={{ padding: 20 }}>
                <label style={labelStyle}>Autor</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <input
                    style={inputStyle}
                    value={form.author.name}
                    onChange={e => updateAuthor('name', e.target.value)}
                    placeholder="Nome do autor"
                  />
                  <input
                    style={inputStyle}
                    value={form.author.role}
                    onChange={e => updateAuthor('role', e.target.value)}
                    placeholder="Cargo"
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      style={{ ...inputStyle, width: 60 }}
                      value={form.author.ini}
                      onChange={e => updateAuthor('ini', e.target.value)}
                      placeholder="IN"
                      maxLength={2}
                    />
                    <input
                      type="color"
                      value={form.author.cor}
                      onChange={e => updateAuthor('cor', e.target.value)}
                      style={{ width: 42, height: 42, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 0 }}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        <style>{`
          @media(max-width:768px){
            .blog-editor-grid{grid-template-columns:1fr!important}
          }
        `}</style>
      </DashboardLayout>
    );
  }

  /* ════════════════════════════════════
     LIST VIEW
  ════════════════════════════════════ */
  return (
    <DashboardLayout title="Editor Blog" subtitle="Gerenciar conteúdo">
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        <div style={{
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '16px 18px',
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--accent)' }}>{posts.length}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Total de Posts</div>
        </div>
        <div style={{
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '16px 18px',
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#3B82F6' }}>
            {posts.filter(p => p.published).length}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Publicados</div>
        </div>
        <div style={{
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '16px 18px',
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#F59E0B' }}>
            {posts.filter(p => !p.published).length}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Rascunhos</div>
        </div>
      </div>

      {/* Toolbar */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)', gap: 10, flexWrap: 'wrap',
        }}>
          <input
            type="text" placeholder="Buscar por título, categoria ou tag..."
            value={busca} onChange={e => setBusca(e.target.value)}
            style={{
              padding: '8px 14px', border: '1.5px solid var(--border)',
              borderRadius: 'var(--radius-xs)', fontFamily: 'var(--font)',
              fontSize: '0.82rem', flex: 1, minWidth: 0, outline: 'none',
            }}
          />
          <Button variant="accent" onClick={handleNew}>
            ✏️ Novo Post
          </Button>
        </div>

        {/* Posts list */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            Carregando...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
            {busca ? 'Nenhum post encontrado.' : 'Nenhum post criado ainda. Clique em "Novo Post" para começar.'}
          </div>
        ) : (
          <div>
            {filtered.map(post => (
              <div
                key={post.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '16px 20px', borderBottom: '1px solid var(--border)',
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                onClick={() => handleEdit(post)}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Cover mini */}
                <div style={{
                  width: 56, height: 38, borderRadius: 6, background: post.coverGradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <span style={{ fontSize: '1.2rem' }}>{post.coverEmoji}</span>
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.08em', color: post.categoryCor,
                    }}>{post.category}</span>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100,
                      background: post.published ? 'rgba(0,214,143,0.1)' : 'rgba(245,158,11,0.1)',
                      color: post.published ? '#059669' : '#D97706',
                    }}>
                      {post.published ? 'Publicado' : 'Rascunho'}
                    </span>
                  </div>
                  <div style={{
                    fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{post.title}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {post.readTime} de leitura · {post.date}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => handleTogglePublish(post)}
                    title={post.published ? 'Despublicar' : 'Publicar'}
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--white)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '0.8rem', cursor: 'pointer',
                    }}
                  >{post.published ? '📤' : '📝'}</button>
                  <button
                    onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                    title="Ver no blog"
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--white)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '0.8rem', cursor: 'pointer',
                    }}
                  >👁️</button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    title="Excluir"
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--white)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '0.8rem', cursor: 'pointer',
                    }}
                  >🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </DashboardLayout>
  );
}